// Timeout + retry wrapper for every Supabase RPC in Game Lite.
//
// Why this exists: the app is played on venue wifi, on phones, in a room full
// of other phones. Two failure shapes bit us in the wild:
//
//   1. A request that never settles. supabase-js sets no fetch timeout, so a
//      half-open socket leaves the promise pending FOREVER — the caller's
//      `submitting` flag never clears and the screen is frozen with no error.
//   2. A request that fails once. A single blip lost a whole answer because the
//      caller logged the error and carried on as if it had saved.
//
// Every RPC now runs with an AbortController deadline and bounded retries with
// exponential backoff + jitter. Retries are only for failures that can plausibly
// succeed on a second attempt — a permission or bad-argument error is returned
// immediately rather than hammered.
//
// The solo write RPCs are all idempotent by construction (migration 125:
// `on conflict … do update`, and finish/complete recompute the totals from
// solo_answers), so retrying a write can never double-count.

import { supabase } from '@tasted/utils/supabase';

export const DEFAULT_TIMEOUT_MS = 12000;
export const DEFAULT_RETRIES = 3;
const BASE_BACKOFF_MS = 400;
const MAX_BACKOFF_MS = 5000;

// PostgREST/Postgres codes that will never succeed on retry — the request is
// wrong, not unlucky. 42501: "not your game" (bad/absent token). 22023: the
// question isn't part of this tasting. PGRST202: no such function.
const PERMANENT_CODES = new Set(['42501', '22023', '23514', 'PGRST202', 'PGRST203', 'PGRST301']);

/**
 * True when an error is worth another attempt.
 *
 * `status === 0` is postgrest-js's signature for a transport-level failure —
 * DNS, dropped connection, or our own abort. Those are exactly the transient
 * ones. 408/429 and any 5xx are server-side transients.
 */
export function isRetryableError(error, status) {
  if (!error) return false;
  if (PERMANENT_CODES.has(String(error.code || ''))) return false;
  if (status === 0 || status == null) return true;
  if (status === 408 || status === 429) return true;
  return status >= 500;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Full jitter: spreads a roomful of phones retrying at once so we don't
// synchronise a thundering herd onto a wifi router that just came back.
function backoffDelay(attempt) {
  const ceiling = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
  return Math.random() * ceiling;
}

/**
 * Run one RPC attempt under a hard deadline.
 * Resolves to postgrest's `{ data, error, status }` — an abort surfaces as a
 * transport error (status 0), which `isRetryableError` treats as retryable.
 */
async function attemptRpc(name, args, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await supabase.rpc(name, args).abortSignal(controller.signal);
    return res || { data: null, error: { message: 'Empty response' }, status: 0 };
  } catch (err) {
    // The sentinel client (missing env) rejects instead of resolving, and a
    // thrown abort lands here on some polyfills. Normalise to the same shape.
    return {
      data: null,
      error: { message: err?.message || String(err), code: err?.code || '' },
      status: 0,
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Call an RPC with a deadline and bounded retries.
 *
 * @param {string} name RPC name
 * @param {object} args RPC arguments
 * @param {object} [opts]
 * @param {number} [opts.timeoutMs] per-attempt deadline
 * @param {number} [opts.retries]   extra attempts after the first
 * @param {(info: {attempt: number, error: object}) => void} [opts.onRetry]
 * @returns {Promise<{data: any, error: object|null, status: number, attempts: number}>}
 *          Never throws — callers decide what a failure means for the UI.
 */
export async function callRpc(name, args, opts = {}) {
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retries = DEFAULT_RETRIES,
    onRetry,
  } = opts;

  let last = { data: null, error: { message: 'not attempted' }, status: 0 };

  for (let attempt = 0; attempt <= retries; attempt++) {
    // Don't burn an attempt (and its full timeout) while the device knows it is
    // offline — wait for the browser to say it's back, up to this attempt's
    // backoff, then try anyway.
    if (typeof navigator !== 'undefined' && navigator.onLine === false && attempt > 0) {
      await waitForOnline(backoffDelay(attempt));
    }

    last = await attemptRpc(name, args, timeoutMs);
    if (!last.error) return { ...last, attempts: attempt + 1 };
    if (!isRetryableError(last.error, last.status)) {
      return { ...last, attempts: attempt + 1 };
    }
    if (attempt < retries) {
      if (onRetry) {
        try {
          onRetry({ attempt: attempt + 1, error: last.error });
        } catch {
          /* a reporting callback must never break the call */
        }
      }
      await sleep(backoffDelay(attempt));
    }
  }

  return { ...last, attempts: retries + 1 };
}

/**
 * The same deadline + retry policy for a plain PostgREST query.
 *
 * Takes a FACTORY, not a builder: a postgrest builder is a one-shot thenable, so
 * a retry has to build a fresh one. The factory receives the abort signal to
 * attach.
 *
 * @param {(signal: AbortSignal) => PromiseLike<{data:any,error:any,status:number}>} build
 * @param {object} [opts] same shape as callRpc's
 */
export async function callQuery(build, opts = {}) {
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retries = DEFAULT_RETRIES,
    onRetry,
  } = opts;

  let last = { data: null, error: { message: 'not attempted' }, status: 0 };

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false && attempt > 0) {
      await waitForOnline(backoffDelay(attempt));
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      last = (await build(controller.signal)) || {
        data: null,
        error: { message: 'Empty response' },
        status: 0,
      };
    } catch (err) {
      last = {
        data: null,
        error: { message: err?.message || String(err), code: err?.code || '' },
        status: 0,
      };
    } finally {
      clearTimeout(timer);
    }

    if (!last.error) return { ...last, attempts: attempt + 1 };
    if (!isRetryableError(last.error, last.status)) return { ...last, attempts: attempt + 1 };
    if (attempt < retries) {
      if (onRetry) {
        try {
          onRetry({ attempt: attempt + 1, error: last.error });
        } catch {
          /* reporting must never break the call */
        }
      }
      await sleep(backoffDelay(attempt));
    }
  }

  return { ...last, attempts: retries + 1 };
}

/**
 * The same deadline + retry policy for an edge function.
 *
 * `supabase.functions.invoke` has no timeout either, and the one call that
 * used it — the prize email — could hang exactly as an RPC could. A function
 * that returns a 4xx is the caller's fault and is not retried; 5xx and
 * transport failures are.
 *
 * @returns {Promise<{data:any, error:object|null, attempts:number}>} Never throws.
 */
export async function callFunction(name, body, opts = {}) {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, retries = 2, onRetry } = opts;
  let last = { data: null, error: { message: 'not attempted' } };

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false && attempt > 0) {
      await waitForOnline(backoffDelay(attempt));
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await supabase.functions.invoke(name, { body, signal: controller.signal });
      last = res || { data: null, error: { message: 'Empty response' } };
    } catch (err) {
      last = { data: null, error: { message: err?.message || String(err), status: 0 } };
    } finally {
      clearTimeout(timer);
    }

    if (!last.error) return { ...last, attempts: attempt + 1 };
    // FunctionsHttpError carries the status on `context`; a transport failure
    // has none, and is worth another go.
    const status = last.error?.context?.status ?? last.error?.status ?? 0;
    const retryable = status === 0 || status === 408 || status === 429 || status >= 500;
    if (!retryable) return { ...last, attempts: attempt + 1 };
    if (attempt < retries) {
      if (onRetry) {
        try {
          onRetry({ attempt: attempt + 1, error: last.error });
        } catch {
          /* reporting must never break the call */
        }
      }
      await sleep(backoffDelay(attempt));
    }
  }
  return { ...last, attempts: retries + 1 };
}

/**
 * Resolve as soon as the browser reports it is back online, or after `maxWaitMs`
 * — whichever comes first. Avoids spending the whole retry budget on attempts
 * that cannot possibly reach the network.
 */
export function waitForOnline(maxWaitMs) {
  if (typeof window === 'undefined') return Promise.resolve();
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.removeEventListener('online', finish);
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(finish, maxWaitMs);
    window.addEventListener('online', finish);
  });
}
